/**
 * Generates a screen size object based on the provided properties from input object.
 *
 * @param obj An object containing screen size properties.
 * @param prop The base property name to retrieve size values from the object.
 * @param suffix An optional suffix to append to the property names.
 * @param cssProp An optional CSS property name to be used in the returned object. Defaults to 'size'.
 * @param defaultVal An optional default value to be used if a size property is not found. Defaults to 'normal'.
 * @returns An object containing the generated screen size properties with their respective values.
 */
export const getScreenSizeObject = (
  obj: Record<any, any>,
  prop: string,
  suffix: string = '',
  cssProp: string = 'size',
  defaultVal: string = 'normal'
): object => {
  const defaultSize = obj[`${prop}`] ?? false;
  const smSize = obj[`${prop}Small${suffix}`] ?? false;
  const mdSize = obj[`${prop}Medium${suffix}`] ?? false;
  const lgSize = obj[`${prop}Large${suffix}`] ?? false;

  if (
    defaultSize ||
    (!mdSize && !lgSize) ||
    (smSize?.value === mdSize?.value && mdSize?.value === lgSize?.value) ||
    (smSize?.value === mdSize?.value && !lgSize)
  ) {
    return {
      [`${cssProp}Sm`]: defaultSize?.value ?? smSize?.value ?? defaultVal,
    };
  }

  if (smSize?.value === mdSize?.value && lgSize) {
    return {
      [`${cssProp}Sm`]: smSize?.value ?? defaultVal,
      [`${cssProp}Lg`]: lgSize?.value ?? defaultVal,
    };
  }

  if (mdSize?.value === lgSize?.value) {
    return {
      [`${cssProp}Sm`]: smSize?.value ?? defaultVal,
      [`${cssProp}Md`]: mdSize?.value ?? defaultVal,
    };
  }

  return {
    [`${cssProp}Sm`]: smSize?.value ?? defaultVal,
    [`${cssProp}Md`]: mdSize?.value ?? defaultVal,
    [`${cssProp}Lg`]: lgSize?.value ?? defaultVal,
  };
};
